import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, DollarSign, Clock, AlertCircle, CheckCircle, XCircle, Calendar, Mail, Phone, CreditCard } from 'lucide-react';
import { pagesAPI } from '../services/api';

const RefundPolicy = () => {
  const [pageData, setPageData] = useState({
    title: 'Refund Policy',
    subtitle: 'We want you to be completely satisfied with our services',
    lastUpdated: 'December 1, 2024',
    companyName: 'Niswa Digital Solutions',
    contactEmail: 'refunds@example.com',
    contactPhone: '+1 (555) 123-4567',
    generalPolicy: 'We offer a 30-day money-back guarantee on all our services. If you are not satisfied with our work, we will refund your payment in full.',
    sections: [
      {
        id: 'eligibility',
        title: 'Refund Eligibility',
        content: 'To be eligible for a refund, you must request it within 30 days of your purchase and meet the following criteria:',
        items: [
          'The service has not been completed or delivered',
          'You have not used or accessed the completed work',
          'The request is made within the 30-day period',
          'The reason for refund is valid and documented'
        ],
        icon: CheckCircle
      },
      {
        id: 'exclusions',
        title: 'Non-Refundable Items',
        content: 'The following items are not eligible for refunds:',
        items: [
          'Completed and delivered projects',
          'Custom designs or work that has been approved',
          'Domain registrations and hosting fees',
          'Third-party software licenses',
          'Consultation fees for completed sessions'
        ],
        icon: XCircle
      },
      {
        id: 'process',
        title: 'Refund Process',
        content: 'To request a refund, please follow these steps:',
        steps: [
          { title: 'Contact Us', description: 'Send an email to our support team with your refund request' },
          { title: 'Provide Details', description: 'Include your order number, reason for refund, and any relevant documentation' },
          { title: 'Review Period', description: 'We will review your request within 3-5 business days' },
          { title: 'Processing', description: 'If approved, refunds are processed within 7-10 business days' }
        ],
        icon: RefreshCw
      },
      {
        id: 'timeline',
        title: 'Refund Timeline',
        content: 'Here\'s what you can expect during the refund process:',
        timeline: [
          { time: 'Immediate', action: 'Submit refund request' },
          { time: '3-5 days', action: 'Review and decision' },
          { time: '7-10 days', action: 'Refund processing' },
          { time: '5-7 days', action: 'Bank processing time' }
        ],
        icon: Clock
      },
      {
        id: 'partial-refunds',
        title: 'Partial Refunds',
        content: 'In some cases, we may offer partial refunds for partially completed work or services that have been partially used.',
        icon: DollarSign
      },
      {
        id: 'disputes',
        title: 'Dispute Resolution',
        content: 'If you disagree with our refund decision, you may request a review by our management team.',
        icon: AlertCircle
      }
    ]
  });

  const [activeSection, setActiveSection] = useState('eligibility');

  useEffect(() => {
    // Fetch page data from API
    const fetchPageData = async () => {
      try {
        const response = await pagesAPI.getByType('refund-policy');
        if (response?.data?.success) {
          const apiData = response.data.data;
          
          // Map API data to component state, ensuring icons are preserved
          const mappedSections = (apiData.content?.sections || pageData.sections).map(section => {
            // Find the corresponding default section to get the icon
            const defaultSection = pageData.sections.find(s => s.id === section.id);
            return {
              ...section,
              icon: defaultSection?.icon || RefreshCw // Fallback to RefreshCw if no icon found
            };
          });
          
          setPageData({
            title: apiData.title || pageData.title,
            subtitle: apiData.subtitle || pageData.subtitle,
            lastUpdated: apiData.last_updated || pageData.lastUpdated,
            companyName: apiData.content?.companyName || pageData.companyName,
            contactEmail: apiData.content?.contactEmail || pageData.contactEmail,
            contactPhone: apiData.content?.contactPhone || pageData.contactPhone,
            generalPolicy: apiData.content?.generalPolicy || pageData.generalPolicy,
            sections: mappedSections
          });
        }
      } catch (error) {
        console.error('Error fetching page data:', error);
      }
    };

    fetchPageData();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      RefreshCw,
      DollarSign,
      Clock,
      AlertCircle,
      CheckCircle,
      XCircle,
      Calendar,
      Mail,
      Phone,
      CreditCard
    };
    return iconMap[iconName] || RefreshCw; // Fallback to RefreshCw
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 pt-20">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block mb-6"
            >
              <RefreshCw className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto" />
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {pageData.title}
            </h1>
            
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">
              {pageData.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Last Updated: {pageData.lastUpdated}</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span>{pageData.companyName}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Table of Contents</h2>
              <nav className="space-y-2">
                {pageData.sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                      activeSection === section.id
                        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="lg:col-span-3"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              {/* General Policy */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">General Policy</h2>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {pageData.generalPolicy}
                </p>
              </motion.div>

              {pageData.sections.map((section, index) => {
                const IconComponent = typeof section.icon === 'string' ? getIconComponent(section.icon) : (section.icon || RefreshCw);
                
                return (
                  <motion.div
                    key={section.id}
                    id={section.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
                    className="mb-12 last:mb-0"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <IconComponent className="w-6 h-6 text-green-600 dark:text-green-400" />
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {section.title}
                      </h2>
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                      {section.content}
                    </p>

                    {section.items && (
                      <div className="space-y-3">
                        {section.items.map((item, itemIndex) => (
                          <motion.div
                            key={itemIndex}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.0 + index * 0.1 + itemIndex * 0.1, duration: 0.6 }}
                            className="flex items-start gap-3"
                          >
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                              section.id === 'exclusions' ? 'bg-red-500 dark:bg-red-400' : 'bg-green-500 dark:bg-green-400'
                            }`}></div>
                            <p className="text-gray-700 dark:text-gray-300">{item}</p>
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {section.steps && (
                      <div className="space-y-4">
                        {section.steps.map((step, stepIndex) => (
                          <motion.div
                            key={stepIndex}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.0 + index * 0.1 + stepIndex * 0.1, duration: 0.6 }}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                          >
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                              {step.title}
                            </h3>
                            <p className="text-gray-700 dark:text-gray-300">
                              {step.description}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {section.timeline && (
                      <div className="space-y-4">
                        {section.timeline.map((timelineItem, timelineIndex) => (
                          <motion.div
                            key={timelineIndex}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.0 + index * 0.1 + timelineIndex * 0.1, duration: 0.6 }}
                            className="flex items-center gap-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                          >
                            <div className="bg-green-500 dark:bg-green-400 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                              {timelineIndex + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">{timelineItem.time}</p>
                              <p className="text-gray-700 dark:text-gray-300">{timelineItem.action}</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                );
              })}

              {/* Contact Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-6 mt-8"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">Email</p>
                      <a href={`mailto:${pageData.contactEmail}`} className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300">
                        {pageData.contactEmail}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">Phone</p>
                      <a href={`tel:${pageData.contactPhone}`} className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300">
                        {pageData.contactPhone}
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy; 